import React, { useEffect, useState } from "react";
import { Box, HStack, Progress, Spinner, Text, VStack } from "@chakra-ui/react";
import type { PollRecord } from "../flow/types";
import { formatDuration } from "../flow/estimateTime";

interface Props {
  poll: PollRecord;
  active: boolean;
}

const useNow = (enabled: boolean, tickMs = 100) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [enabled, tickMs]);
  return now;
};

const PollingIndicator: React.FC<Props> = ({ poll, active }) => {
  const now = useNow(active);
  const remainingMs = poll.nextCheckAt ? Math.max(0, poll.nextCheckAt - now) : 0;
  const waitMs = poll.currentWaitMs || 1;
  const progress = poll.nextCheckAt ? 100 - (remainingMs / waitMs) * 100 : 100;
  const elapsed = (poll.endedAt ?? now) - poll.startedAt;

  return (
    <Box
      borderWidth="1px"
      borderColor={active ? "orange.200" : "gray.200"}
      bg={active ? "orange.50" : "gray.50"}
      borderRadius="md"
      p={3}
      fontSize="xs"
    >
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          {active && <Spinner size="xs" color="orange.500" speed="0.8s" />}
          <Text fontWeight="700" color={active ? "orange.700" : "gray.700"}>
            {active ? "Waiting for event…" : poll.outcome === "success" ? "Event observed" : poll.outcome === "timeout" ? "Event timed out" : "Polling stopped"}
          </Text>
        </HStack>
        <Text color="gray.600" fontFamily="mono">
          Attempt {poll.attempts} / {poll.maxAttempts} · {formatDuration(elapsed)}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={1} color="gray.700">
        <HStack align="baseline">
          <Text w="120px" color="gray.500">
            Checking
          </Text>
          <Text>{poll.expected}</Text>
        </HStack>
        <HStack align="baseline">
          <Text w="120px" color="gray.500">
            Trigger
          </Text>
          <Text>{poll.trigger}</Text>
        </HStack>
        <HStack align="baseline">
          <Text w="120px" color="gray.500">
            Last observed
          </Text>
          <Text fontFamily="mono">{poll.lastObserved || "—"}</Text>
        </HStack>
        {active && poll.nextCheckAt && (
          <HStack align="baseline">
            <Text w="120px" color="gray.500">
              Next check in
            </Text>
            <Text fontFamily="mono" fontWeight="600">
              {(remainingMs / 1000).toFixed(1)}s
            </Text>
          </HStack>
        )}
      </VStack>
      {active && (
        <Progress
          mt={2}
          size="xs"
          value={Math.min(100, Math.max(0, progress))}
          colorScheme="orange"
          borderRadius="full"
          sx={{ "& > div": { transition: "width 0.1s linear" } }}
        />
      )}
    </Box>
  );
};

export default PollingIndicator;
